import { ChangeDetectionStrategy, Component, OnInit, TemplateRef, computed, inject, input, output, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { TranslocoService } from '@ngneat/transloco';
import moment from 'moment';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { momentLocaleFor } from '@shared/utils/moment-locale.utils';
import {
  HD_MOMENT_CATEGORIES,
  hdMomentCategoryLabelKey,
} from '../bodygraph/advanced-properties/hd-moment-fields';
import { HdLifeTimelineMarker } from './hd-life-timeline.models';

/** Maximaal aantal stippen per dagcel; meer wordt een "+n"-teller. */
const MAX_DOTS_PER_DAY = 3;
/** Maximaal aantal planeetglyphs per dagcel (transits-variant); meer wordt "+n". */
const MAX_BADGES_PER_DAY = 6;
const MONTH_KEY_FORMAT = 'YYYY-MM';

/** Glyph-badge in een dagcel (transits-variant: een planeet die die dag wisselt). */
export interface HdCalendarDayBadge {
  readonly id: string;
  readonly glyph: string;
  readonly color: string;
  readonly tooltip: string;
  /** Nadrukkelijk (poortwissel) of subtiel (enkel lijnwissels). */
  readonly strong: boolean;
}

/** Context van de `dayCorner`-template: de lokale dagsleutel (`YYYY-MM-DD`) van de cel. */
export interface HdCalendarDayCornerContext {
  readonly $implicit: string;
}

interface DayVm {
  /** Lokale dagsleutel "YYYY-MM-DD". */
  readonly key: string;
  readonly dayNumber: number;
  readonly inMonth: boolean;
  readonly isToday: boolean;
  /** Momenten die op deze dag starten (stippen). */
  readonly starts: readonly HdLifeTimelineMarker[];
  /** Deze dag valt binnen een range (subtiele bandmarkering, beslispunt 10). */
  readonly inRange: boolean;
  /** Transits-variant: de dag bovenaan de dag-flow. */
  readonly isFocus: boolean;
  /** Transits-variant: zichtbare planeetglyphs (max {@link MAX_BADGES_PER_DAY}). */
  readonly badges: readonly HdCalendarDayBadge[];
  readonly badgeOverflow: number;
}

/**
 * Kalenderweergave van gelogde momenten (spec docs/LEVENSTIJDLIJN.md §2.7):
 * maandgrid ma-zo met gekleurde stippen per dag (momentkleur), subtiele
 * bandmarkering voor range-dagen en een dagpaneel met de momenten van de
 * aangeklikte dag. Dom component: de host levert markers en handelt
 * openen (mét Notities-paneel) en toevoegen (categorie daily) af. De
 * compact-variant past in de Momenten-sectie van het Geavanceerd-paneel.
 *
 * **Transits-variant** (`variant="transits"`, verticale transits-weergave,
 * spec 2026-09-26 §2.4): zelfde grid en navigatie, maar per dag de
 * planeetglyphs van de planeten die die dag van poort (of lijn) wisselen
 * (`dayBadges`), zonder momenten-categoriefilter en zonder dagpaneel — een
 * dagklik meldt `daySelected` zodat de dag-flow erheen springt. Bladeren meldt
 * `visibleMonthChanged` (de host laadt er de wissels voor); extra toolbar-
 * inhoud (de poort/lijn-schakelaar) komt binnen via `[hdCalendarToolbarEnd]`.
 * Rechtsboven in elke dagcel tekent de host desgewenst eigen inhoud via
 * `dayCorner` (transits: de lijn-dag van de zon).
 */
@Component({
  selector: 'hd-moments-calendar',
  standalone: true,
  imports: [NgTemplateOutlet],
  templateUrl: './hd-moments-calendar.component.html',
  styleUrl: './hd-moments-calendar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdMomentsCalendarComponent implements OnInit {
  readonly markers = input<readonly HdLifeTimelineMarker[]>([]);
  /** Compacte variant voor het dockpaneel (Momenten-sectie). */
  readonly compact = input(false);
  readonly variant = input<'moments' | 'transits'>('moments');
  /** Transits-variant: planeetglyphs per lokale dagsleutel (`YYYY-MM-DD`). */
  readonly dayBadges = input<ReadonlyMap<string, readonly HdCalendarDayBadge[]> | null>(null);
  /** Transits-variant: dag bovenaan de dag-flow (subtiele markering). */
  readonly focusDayKey = input<string | null>(null);
  /**
   * Vandaag volgens de host (lokale dagsleutel); zonder waarde de echte dag. De
   * transits geven hun kijktijd mee (tijdslider), zodat Vandaag hier op
   * dezelfde dag landt als in de weekstrip.
   */
  readonly todayKey = input<string | null>(null);
  /** Startmaand (`YYYY-MM`), één keer gelezen; zonder waarde de huidige maand. */
  readonly initialMonthKey = input<string | null>(null);
  /**
   * Inhoud rechtsboven in elke dagcel, door de host getekend met de dagsleutel
   * als context (transits: de lijn-dag van de zon). De cel is de container
   * `hd-calendar-day`, zodat die inhoud zich met container queries aan een
   * smalle cel kan aanpassen.
   */
  readonly dayCorner = input<TemplateRef<HdCalendarDayCornerContext> | null>(null);
  /** Een dag werd aangeklikt (lokale dagsleutel). */
  readonly daySelected = output<string>();
  /** De getoonde maand veranderde door te bladeren (`YYYY-MM`). */
  readonly visibleMonthChanged = output<string>();

  /** Openen vanuit de kalender = altijd mét het Notities-paneel (spec §2.9). */
  readonly openRequested = output<string>();
  /** Openen van een I Tjing-moment: de raadpleging (host opent de I Tjing-momentdialoog). */
  readonly consultationOpenRequested = output<string>();
  /** Potlood in het dagpaneel: momentdetails bewerken (host opent de editdialoog). */
  readonly editRequested = output<string>();
  /** Moment toevoegen op deze dag (ISO-utc, middag lokale tijd); host preselecteert daily. */
  readonly addRequested = output<string>();

  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /** Eerste dag van de getoonde maand (lokale tijd, ms). */
  protected readonly monthStartMs = signal(moment().startOf('month').valueOf());
  protected readonly selectedDayKey = signal<string | null>(null);

  /**
   * Grid-modus: dagen, maandkiezer of jaarkiezer (finetune 2026-09-21) — het
   * bekende datepicker-drilldown-patroon: klik op de maandnaam kiest snel een
   * maand, klik op het jaartal een jaar (jaar → maand → dagen).
   */
  protected readonly pickerMode = signal<'days' | 'months' | 'years'>('days');

  /** Categoriefilter: in de kalender staat default álles aan (de kalender is juist de daily-view). */
  protected readonly hiddenCategories = signal<ReadonlySet<string>>(new Set());
  protected readonly categories = HD_MOMENT_CATEGORIES;

  protected readonly isTransits = computed(() => this.variant() === 'transits');

  ngOnInit(): void {
    const initial = this.initialMonthKey();
    if (initial == null) return;
    const month = moment(initial, MONTH_KEY_FORMAT, true);
    if (month.isValid()) this.monthStartMs.set(month.startOf('month').valueOf());
  }

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      previous: translate('humanDesign.momentsCalendar.previous'),
      next: translate('humanDesign.momentsCalendar.next'),
      today: translate('humanDesign.momentsCalendar.today'),
      addMoment: translate('humanDesign.momentsCalendar.addMoment'),
      empty: translate('humanDesign.momentsCalendar.empty'),
      open: translate('humanDesign.lifeTimeline.open'),
      pickMonth: translate('humanDesign.momentsCalendar.pickMonth'),
      pickYear: translate('humanDesign.momentsCalendar.pickYear'),
      edit: translate('humanDesign.lifeTimeline.edit'),
      category: Object.fromEntries(HD_MOMENT_CATEGORIES.map(category =>
        [category, translate(hdMomentCategoryLabelKey(category))])) as Record<string, string>,
    };
  });

  /** Moment-locale van de content-taal (de globale volgt een taalwissel niet). */
  private readonly locale = computed(() => momentLocaleFor(this.contentLang.effectiveLang()));

  protected readonly monthName = computed(() => moment(this.monthStartMs()).locale(this.locale()).format('MMMM'));
  protected readonly yearLabel = computed(() => moment(this.monthStartMs()).format('YYYY'));

  /** De twaalf maanden van het getoonde jaar (maandkiezer). */
  protected readonly monthOptions = computed(() => {
    const base = moment(this.monthStartMs()).locale(this.locale());
    const today = moment();
    return Array.from({ length: 12 }, (_, index) => ({
      index,
      label: base.clone().month(index).format('MMM'),
      isSelected: base.month() === index,
      isCurrent: today.year() === base.year() && today.month() === index,
    }));
  });

  /** Twaalf-jarenblok rond het getoonde jaar (jaarkiezer; ‹ › bladert per blok). */
  protected readonly yearOptions = computed(() => {
    const shownYear = moment(this.monthStartMs()).year();
    const blockStart = Math.floor(shownYear / 12) * 12;
    const currentYear = moment().year();
    return Array.from({ length: 12 }, (_, index) => {
      const year = blockStart + index;
      return { year, isSelected: year === shownYear, isCurrent: year === currentYear };
    });
  });

  /** Zichtbare markers na het categoriefilter (zonder categorie = lifetime). */
  private readonly visibleMarkers = computed(() => {
    const hidden = this.hiddenCategories();
    return this.markers().filter(marker => !hidden.has(marker.category ?? 'lifetime'));
  });

  /** Weken (ma-zo) van de getoonde maand, elke cel met zijn momenten. */
  protected readonly weeks = computed<DayVm[][]>(() => {
    const markers = this.visibleMarkers();
    const startsByDay = new Map<string, HdLifeTimelineMarker[]>();
    const ranges: { fromKey: string; toKey: string }[] = [];
    for (const marker of markers) {
      const startKey = moment.utc(marker.startUtc).local().format('YYYY-MM-DD');
      const existing = startsByDay.get(startKey);
      if (existing) existing.push(marker);
      else startsByDay.set(startKey, [marker]);
      if (marker.kind === 'range' && marker.endUtc) {
        ranges.push({ fromKey: startKey, toKey: moment.utc(marker.endUtc).local().format('YYYY-MM-DD') });
      }
    }

    const monthStart = moment(this.monthStartMs());
    const gridStart = monthStart.clone().startOf('isoWeek');
    const todayKey = this.currentTodayKey();
    const focusKey = this.focusDayKey();
    const badgesByDay = this.dayBadges();
    const weeks: DayVm[][] = [];
    const cursor = gridStart.clone();
    for (let week = 0; week < 6; week++) {
      const days: DayVm[] = [];
      for (let day = 0; day < 7; day++) {
        const key = cursor.format('YYYY-MM-DD');
        const badges = badgesByDay?.get(key) ?? [];
        days.push({
          key,
          dayNumber: cursor.date(),
          inMonth: cursor.month() === monthStart.month(),
          isToday: key === todayKey,
          starts: startsByDay.get(key) ?? [],
          inRange: ranges.some(range => key >= range.fromKey && key <= range.toKey),
          isFocus: key === focusKey,
          badges: badges.slice(0, MAX_BADGES_PER_DAY),
          badgeOverflow: Math.max(0, badges.length - MAX_BADGES_PER_DAY),
        });
        cursor.add(1, 'day');
      }
      weeks.push(days);
    }
    return weeks;
  });

  /** Ma-zo-koppen uit de moment-locale. */
  protected readonly weekdayLabels = computed(() => {
    const start = moment(this.monthStartMs()).locale(this.locale()).startOf('isoWeek');
    return Array.from({ length: 7 }, (_, index) => start.clone().add(index, 'day').format('dd'));
  });

  protected readonly selectedDay = computed<DayVm | null>(() => {
    const key = this.selectedDayKey();
    if (!key) return null;
    for (const week of this.weeks()) {
      const day = week.find(candidate => candidate.key === key);
      if (day) return day;
    }
    return null;
  });

  protected dotColor(marker: HdLifeTimelineMarker): string {
    return marker.colorHex ?? 'var(--amber, #d4a869)';
  }

  protected dotsFor(day: DayVm): readonly HdLifeTimelineMarker[] {
    return day.starts.slice(0, MAX_DOTS_PER_DAY);
  }

  protected overflowCount(day: DayVm): number {
    return Math.max(0, day.starts.length - MAX_DOTS_PER_DAY);
  }

  protected markerTimeText(marker: HdLifeTimelineMarker): string {
    return moment.utc(marker.startUtc).local().format('HH:mm');
  }

  protected shiftMonth(delta: number): void {
    this.showMonth(moment(this.monthStartMs()).add(delta, 'month'));
    this.selectedDayKey.set(null);
  }

  /** ‹ › volgt de modus: dagen per maand, maandkiezer per jaar, jaarkiezer per 12 jaar. */
  protected navigate(delta: number): void {
    const mode = this.pickerMode();
    if (mode === 'days') {
      this.shiftMonth(delta);
      return;
    }
    const years = mode === 'months' ? delta : delta * 12;
    this.showMonth(moment(this.monthStartMs()).add(years, 'year'));
    this.selectedDayKey.set(null);
  }

  /**
   * Toont de maand van vandaag. Transits: ook de dag-flow springt naar vandaag
   * (zoals Vandaag in de weekstrip); anders deed de knop niets zolang die maand
   * al getoond werd.
   */
  protected goToToday(): void {
    const todayKey = this.currentTodayKey();
    this.showMonth(moment(todayKey, 'YYYY-MM-DD'));
    this.selectedDayKey.set(null);
    this.pickerMode.set('days');
    if (this.isTransits()) this.daySelected.emit(todayKey);
  }

  private currentTodayKey(): string {
    return this.todayKey() ?? moment().format('YYYY-MM-DD');
  }

  /** Toont de maand van `month` en meldt een effectieve maandwissel aan de host. */
  private showMonth(month: moment.Moment): void {
    const next = month.clone().startOf('month').valueOf();
    if (next === this.monthStartMs()) return;
    this.monthStartMs.set(next);
    this.visibleMonthChanged.emit(moment(next).format(MONTH_KEY_FORMAT));
  }

  /** Klik op de maandnaam: maandkiezer openen (nogmaals klikken sluit hem). */
  protected toggleMonthPicker(): void {
    this.pickerMode.set(this.pickerMode() === 'months' ? 'days' : 'months');
  }

  /** Klik op het jaartal: jaarkiezer openen (nogmaals klikken sluit hem). */
  protected toggleYearPicker(): void {
    this.pickerMode.set(this.pickerMode() === 'years' ? 'days' : 'years');
  }

  protected pickMonth(monthIndex: number): void {
    this.showMonth(moment(this.monthStartMs()).month(monthIndex));
    this.selectedDayKey.set(null);
    this.pickerMode.set('days');
  }

  /** Jaar gekozen → door naar de maandkiezer van dat jaar (drilldown). */
  protected pickYear(year: number): void {
    this.showMonth(moment(this.monthStartMs()).year(year));
    this.selectedDayKey.set(null);
    this.pickerMode.set('months');
  }

  /** Momenten: dagpaneel open/dicht. Transits: de host laat de dag-flow erheen springen. */
  protected selectDay(day: DayVm): void {
    this.daySelected.emit(day.key);
    if (this.isTransits()) return;
    this.selectedDayKey.set(this.selectedDayKey() === day.key ? null : day.key);
  }

  protected toggleCategoryFilter(category: string): void {
    const next = new Set(this.hiddenCategories());
    if (next.has(category)) next.delete(category);
    else next.add(category);
    this.hiddenCategories.set(next);
  }

  protected openMarker(marker: HdLifeTimelineMarker): void {
    if (marker.consultationId) {
      this.consultationOpenRequested.emit(marker.consultationId);
      return;
    }
    if (marker.openChartId) this.openRequested.emit(marker.openChartId);
  }

  protected editMarker(marker: HdLifeTimelineMarker): void {
    if (marker.openChartId) this.editRequested.emit(marker.openChartId);
  }

  /** "+ moment" op de gekozen dag: middag lokale tijd als neutraal prefill-anker. */
  protected addOnSelectedDay(): void {
    const key = this.selectedDayKey();
    if (!key) return;
    this.addRequested.emit(moment(`${key}T12:00`, 'YYYY-MM-DDTHH:mm').toISOString());
  }
}
